import axios from "axios";
import { StorageBox } from "./stroage";
import { FUEL_MS_URL } from "./constants";
import {
  endSession,
  isAccessTokenExpiring,
  refreshAuthV2Session,
} from "./authSession";

// One refresh at a time; concurrent requests wait on the same promise.
let refreshing: Promise<string | null> | null = null;
const refreshOnce = () => {
  refreshing ??= refreshAuthV2Session().finally(() => {
    refreshing = null;
  });
  return refreshing;
};

// Routes that issue or revoke tokens — never refresh or retry these.
// (/auth/v2/me is a normal authenticated call and is left out.)
const TOKEN_ROUTES = [
  "/auth/v2/login",
  "/auth/v2/register",
  "/auth/v2/otp/",
  "/auth/v2/password/",
  "/auth/v2/refresh",
  "/auth/v2/logout",
];
const isAuthRoute = (url?: string) =>
  !!url && TOKEN_ROUTES.some((route) => url.includes(route));

// The backend answers an expired local token with 403 "Invalid authorization
// information." (its legacy fallback), not 401 — treat both as expired.
const isExpiredSessionError = (error: any) => {
  const status = error?.response?.status;
  return (
    status === 401 ||
    (status === 403 &&
      error?.response?.data?.message === "Invalid authorization information.")
  );
};

axios.interceptors.request.use(async (config) => {
  if (
    !isAuthRoute(config.url) &&
    StorageBox.getRefreshToken() &&
    isAccessTokenExpiring()
  ) {
    const token = await refreshOnce();
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    if (
      !original ||
      original._retried ||
      isAuthRoute(original.url) ||
      !isExpiredSessionError(error)
    ) {
      return Promise.reject(error);
    }
    original._retried = true;
    const token = await refreshOnce();
    if (!token) {
      endSession();
      return Promise.reject(error);
    }
    original.headers.Authorization = `Bearer ${token}`;
    return axios(original);
  },
);

const authHeaders = () => ({
  headers: {
    Authorization: `Bearer ${StorageBox.getAccessToken()}`,
  },
});

//Axios Get
const get_api = (route: string) => {
  return axios.get(FUEL_MS_URL + route, authHeaders());
};

//Axios Post
const post_api = (route: string, data: any) => {
  return axios.post(FUEL_MS_URL + route, data, authHeaders());
};

//Axios Put
const put_api = (route: string, data: any) => {
  return axios.put(FUEL_MS_URL + route, data, authHeaders());
};

//Axios Delete
const delete_api = (route: string, data: any) => {
  return axios({
    method: "delete",
    url: FUEL_MS_URL + route,
    headers: {
      ...authHeaders().headers,
      "Content-Type": "application/json",
    },
    data: data,
  });
};

const BaseService = {
  post_api,
  get_api,
  put_api,
  delete_api,
};

export default BaseService;
